/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Box, Typography, useTheme } from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { colorTheme } from 'theme';
import { strings } from 'i18n/Strings';
import { CustomDataGrid } from 'components';
import { commonConstantTexts } from 'constants/constantText';
import { type GridRowData } from 'components/customDataGrid/CustomDataGrid';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CurrencyFormatterAllDecimals } from 'components/helpers/commonFunctions/CurrencyFormater';
import { getAllTodayOrderListUsersDetailed } from 'service/OrderService';

interface UserWithUnits {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  employeeContribution?: number;
  buyType?: string;
}

const TodayOrderDetailed = (props: {
  fundRow: { users: UserWithUnits[] };
}): JSX.Element => {
  const { fundRow } = props;
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);
  const [contributionData, setContributionData] = useState([]);
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector(
    (state) => state?.pagination?.todayOrderDetailed,
  );

  const getAllTodayOrderListDetailed = async (): Promise<void> => {
    try {
      const data = await getAllTodayOrderListUsersDetailed(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        Number(fundRow.fundId),
      );
      setContributionData(data?.order);
      setPageCount(data?.count);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void getAllTodayOrderListDetailed();
  }, [pageInfo?.page, pageInfo.pageSize]);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'userId',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.userId}
        </Typography>
      ),
    },
    {
      field: 'userName',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {`${row?.userInfo?.firstName || ''} ${row?.userInfo?.lastName || ''}`}
        </Typography>
      ),
    },
    {
      field: 'buyType',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.orderType}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.transactionInfo?.contributionFileId
            ? strings.displayText.contribution
            : row?.transactionInfo?.isRebalanceTransaction
            ? strings.displayText.rebalance
            : row?.transactionInfo?.isPlanChangeTransaction
            ? strings.displayText.planChangeLabel
            : strings.displayText.transferIn}
        </Typography>
      ),
    },
    {
      field: 'amount',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.amountLabel}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormatterAllDecimals(Number(row.totalContribution ?? 0))}
        </Typography>
      ),
    },
  ];

  return (
    <Box padding={1}>
      <CustomDataGrid
        columns={columns}
        rows={contributionData}
        getRowId={(row: GridRowData) =>
          `${row.userId}-${
            row.transactionInfo?.id ||
            row.transactionInfo?.contributionFileId ||
            row.transactionInfo?.planChangeId ||
            Math.random()
          }`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? commonConstantTexts.evenText
            : commonConstantTexts.oddText
        }
        pageCount={pageCount}
        currentPage="todayOrderDetailed"
      />
    </Box>
  );
};

export default TodayOrderDetailed;
